/* eslint-disable max-lines */
import { Box, Grid } from '@mui/material';
import PropTypes from 'prop-types';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import debounceLodash from 'lodash.debounce';
import LeadGenerationContext from '../../../context/LeadGenerationContext';
import { CustomInput } from '../CustomInput/CustomInput';
import { CustomSelect } from '../CustomSelect/CustomSelect';
import { CustomAutoComplete } from '../CustomAutoComplete/CustomAutoComplete';
import { AddressInformationStyles as classes } from './AddressInformationStyles';
import {
  addAutoCompleteCityToDropdownIfMissing,
  returnCityList,
  returnStateList,
} from './AddressInformationUtils';
import {
  ADDRESS_LINE1,
  ADDRESS_LINE2,
  CITY,
  COUNTRY,
  HOMEUS_PR,
  MANDATORY_FIELD_ERR,
  HOMEUS_VI,
  HOME_ADDRESS_TITLE,
  HOME_PR,
  HOME_VI,
  REQUIRED,
  STATE,
  ZIPCODE,
  UTILITY_BILL,
  ADDRESSLINE1_MAX_ERR,
  LIBRE_AHORRA_BUNDLE,
  EN,
  COUNTRY_ES_BUNDLE,
  COUNTRY_ES,
} from '../../../constants';
import { dgiMapping } from '../../../services/dgiMapping';
import {
  commonFunction,
  commonTernary,
  fetchAddressLookup,
  isBundle,
  handleAddressEditModal,
} from '../../commonCode';
import { commonUtility } from '../UtilityFunctions';
import { productMapping } from '../../../services/productMapping';

/** HomeAddress is a child component to AddressInformation
 */
const HomeAddress = (props) => {
  const { t } = useTranslation();
  const leadGenerationContext = useContext(LeadGenerationContext);
  const [addressLookupDataResponse, setAddressLookupDataResponse] = useState({});
  const [addressLookupData, setAddressLookupData] = useState([]);
  const { handleChange } = props;

  const {
    addressInformation,
    setAddressInformation,
    addressInformationErrors,
    setAddressInformationErrors,
    setIsScreenThreeDirty,
    setIsScreenFourDirty,
    setIsScreenFiveDirty,
    setIsFundingScreenDirty,
    setCurrentPage,
    location,
    productName,
    personalInformation,
    ocrResponse,
    newAddress,
    callLookupApiHome,
    setCallLookupApiHome,
    setCallLookupApi,
    setCallLookupApiEmp,
    setCallLookupApiHomeSelfEmp,
    flowTypeETB,
    language,
  } = leadGenerationContext;

  useEffect(() => {
    setCurrentPage('address-info');
  }, []);

  const handleAutoCompleteData = (addressObject) => {
    setIsScreenThreeDirty(true);
    setIsScreenFourDirty(true);
    setIsScreenFiveDirty(true);
    setIsFundingScreenDirty(true);

    let city = '';
    let state = '';
    let country = '';
    let zipCode = '';
    let address1 = '';
    if (
      addressObject?.Address?.AddressLine1 ||
      addressObject?.Address?.PostalCode ||
      addressObject?.Address?.City
    ) {
      address1 = commonFunction(addressObject?.Address?.AddressLine1);
      zipCode = addressObject?.Address?.PostalCode.split('-')[0];
      city = addressObject?.Address?.City;
    }
    if (addressObject?.Address?.AddressLine1?.length > 30) {
      setAddressInformationErrors((prevState) => ({
        ...prevState,
        address1: ADDRESSLINE1_MAX_ERR,
        city: '',
        country: '',
        state: '',
        zipCode: '',
      }));
    } else {
      setAddressInformationErrors((prevState) => ({
        ...prevState,
        address1: '',
        city: '',
        country: '',
        state: '',
        zipCode: '',
      }));
    }
    if (addressObject?.Address?.CountrySubdivisionCode === HOMEUS_PR) {
      country = 'PR';
    } else if (addressObject?.Address?.CountrySubdivisionCode === HOMEUS_VI) {
      country = 'VI';
    } else {
      country = 'USA';
    }

    if (
      (addressObject?.Address?.State && country === HOME_VI) ||
      (addressObject?.Address?.State && country === HOME_PR)
    ) {
      state = 'N/A';
    } else {
      state = addressObject?.Address?.State;
    }
    const cityPresent = addAutoCompleteCityToDropdownIfMissing(country, state, city);
    if (!cityPresent) {
      setAddressInformation((prevState) => ({
        ...prevState,
        missingAutoCompleteCity: city,
      }));
    }
    setAddressInformation((prevState) => ({
      ...prevState,
      address1,
      city,
      country,
      state,
      zipCode,
    }));
    setAddressInformationErrors((prevState) => ({
      ...prevState,
      city: '',
      country: '',
      state: '',
      zipCode: '',
    }));
    if (addressInformation?.sameMailingAddress) {
      setAddressInformation((prevState) => ({
        ...prevState,
        permanentAddress1: address1,
        permanentCity: city,
        permanentCountry: country,
        permanentState: state,
        permanentZipCode: zipCode,
      }));
    }
  };

  const handleAddressLineOneChange = (address1) => {
    setIsScreenThreeDirty(true);
    setIsScreenFourDirty(true);
    setIsScreenFiveDirty(true);
    setIsFundingScreenDirty(true);
    const input = 'address1';
    if (address1 && address1.length > 0 && address1.length <= 30) {
      if (address1.match(/^[A-Za-z0-9'#@%&\\/.\-\s,]+$/) && address1?.length >= 2) {
        setAddressInformation((prevState) => ({
          ...prevState,
          [input]: address1,
          vouchedAddrEdited: true,
        }));

        setAddressInformationErrors((prevState) => ({
          ...prevState,
          [input]: '',
        }));
        handleAddressEditModal(leadGenerationContext);
        if (addressInformation?.sameMailingAddress) {
          setAddressInformation((prevState) => ({
            ...prevState,
            permanentAddress1: address1,
          }));
        }
      } else {
        setAddressInformation((prevState) => ({
          ...prevState,
          [input]: address1,
        }));
        setAddressInformationErrors((prevState) => ({
          ...prevState,
          [input]: '* Invalid address',
        }));
      }
    } else if (address1 && address1.length > 30) {
      setAddressInformation((prevState) => ({ ...prevState, [input]: address1 }));
      setAddressInformationErrors((prevState) => ({ ...prevState, [input]: ADDRESSLINE1_MAX_ERR }));
    } else {
      setAddressInformation((prevState) => ({
        ...prevState,
        [input]: address1 || '',
      }));
      setAddressInformationErrors((prevState) => ({
        ...prevState,
        [input]: MANDATORY_FIELD_ERR,
      }));
    }
    if (!ocrResponse) {
      setAddressInformation((prevState) => ({
        ...prevState,
        ocrAddressEdit: false,
      }));
    } else {
      setAddressInformation((prevState) => ({
        ...prevState,
        ocrAddressEdit: true,
      }));
    }
  };

  const debounceFunc5 = debounceLodash((address1, country, query5, accessToken) => {
    fetchAddressLookup(
      address1,
      country,
      query5,
      accessToken,
      setAddressLookupData,
      setAddressLookupDataResponse,
      setCallLookupApi,
      setCallLookupApiHome,
      setCallLookupApiEmp,
      setCallLookupApiHomeSelfEmp
    );
  }, 500);

  const fetchAddress5 = useCallback(debounceFunc5, []);

  useEffect(() => {
    let query5 = '';
    if (addressInformation?.address1) {
      query5 += `${addressInformation.address1} `;
    }
    if (addressInformation?.address2) {
      query5 += `${addressInformation.address2} `;
    }
    if (addressInformation?.state) {
      query5 += `${addressInformation.state} `;
    }
    if (addressInformation?.city) {
      query5 += `${addressInformation.city} `;
    }
    if (addressInformation?.zipCode) {
      query5 += `${addressInformation.zipCode} `;
    }
    const address1 = addressInformation?.address1 ? addressInformation.address1 : '';
    const country = addressInformation?.country ? addressInformation.country : '';
    if (callLookupApiHome) {
      fetchAddress5(address1, country, query5, leadGenerationContext.accessToken);
    }
  }, [leadGenerationContext?.accessToken, addressInformation?.address1]);

  let errorMessage;
  const mesgForCountrySel = () => {
    if (addressInformationErrors?.country) {
      errorMessage = t(addressInformationErrors.country, {
        defaultValue: addressInformationErrors.country,
      });
    } else if (location === 'VI') {
      if (addressInformation?.country !== 'VI') {
        errorMessage = t('DefaultCountryVI', {
          defaultValue: 'You must be a Virgin Islands resident to open this account.',
        });
      } else {
        errorMessage = '';
      }
    } else if (dgiMapping(productName)) {
      if (addressInformation?.country !== 'PR') {
        errorMessage = t('DefaultCountryPR', {
          defaultValue: 'You must be a Puerto Rico resident to open this account.',
        });
      } else {
        errorMessage = '';
      }
    } else if (addressInformation?.country === 'VI') {
      errorMessage = t('DefaultCountryPR', {
        defaultValue: 'You must be a Puerto Rico resident to open this account.',
      });
    } else {
      errorMessage = '';
    }
    return errorMessage;
  };

  return (
    <>
      {(((productMapping(productName) || location === HOME_PR || dgiMapping(productName)) &&
        location !== HOME_VI &&
        newAddress) ||
        addressInformation.utilityBill ||
        (addressInformation.vouchedAddressVerified === false && location !== HOME_VI) ||
        addressInformation.retainUtilityBill === true) &&
      productMapping(productName) !== LIBRE_AHORRA_BUNDLE ? (
        <Grid
          container
          size={{ sm: 12 }}
          sx={[classes.input_grid_addr, classes.docUploadSidebarGrid]}
        >
          <Grid size={{ xs: 12 }}>
            <CustomSelect
              id="utilityBill"
              dataTestId="utilityBill_id"
              required={!isBundle(productName)}
              value={commonUtility(addressInformation?.utilityBill)}
              inputLabelAriaLabel={`${t(UTILITY_BILL, {
                defaultValue: UTILITY_BILL,
              })} ${t(REQUIRED, { defaultValue: REQUIRED })}`}
              label={t(UTILITY_BILL, {
                defaultValue: UTILITY_BILL,
              })}
              autoFocus
              options={['LUMA', 'AAA', 'Others']}
              handleChange={handleChange}
              isUtilityBill
              fullWidth
              isBundle={isBundle(productName)}
              selectAriaLabel={`${t('Aria Label Utility Bill', {
                defaultValue: 'Utility Bill Required Select Utility Bill dropdown menu button',
              })} ${t(REQUIRED, { defaultValue: REQUIRED })}`}
              error={addressInformationErrors?.utilityBill}
            />
            <Box component="span" role="alert" id="utilitybill" sx={classes.error_message}>
              {t(addressInformationErrors?.utilityBill, {
                defaultValue: addressInformationErrors?.utilityBill,
              })}
            </Box>
          </Grid>
        </Grid>
      ) : (
        ''
      )}
      <Grid container size={{ sm: 12 }} sx={classes.input_grid_addr}>
        <Grid size={{ xs: 12 }}>
          <CustomAutoComplete
            id="address1"
            dataTestIdAutocomplete="autocomplete_address1"
            dataTestIdForLabel="homeAddress1"
            dataTestIdForInput="addr1Input"
            required={!isBundle(productName)}
            inputLabelAriaLabel={`${t(HOME_ADDRESS_TITLE, {
              defaultValue: HOME_ADDRESS_TITLE,
            })} ${t(ADDRESS_LINE1, {
              defaultValue: ADDRESS_LINE1,
            })} ${t(REQUIRED, { defaultValue: REQUIRED })}`}
            label={
              <span
                style={{
                  color:
                    isBundle(productName) && addressInformationErrors?.address1 ? 'red' : 'inherit',
                }}
              >
                {t(ADDRESS_LINE1, { defaultValue: ADDRESS_LINE1 })}
              </span>
            }
            options={
              addressLookupData &&
              addressInformation?.address1 &&
              addressInformation.address1?.length >= 6
                ? addressLookupData
                : []
            }
            value={commonUtility(addressInformation?.address1)}
            handleChange={(_event, selectedValue) => {
              setAddressInformation((prevState) => ({
                ...prevState,
                editHomeAddress: true,
              }));
              setCallLookupApiHome(true);
              if (addressInformation?.address1 && addressInformation.address1?.length >= 6) {
                const index = addressLookupData.indexOf(selectedValue);
                const selectedObj = addressLookupDataResponse.data?.Results[index];
                handleAutoCompleteData(selectedObj);
              }
            }}
            handleInputChange={(event, newInputValue) => {
              if (event) {
                if (isBundle(productName)) {
                  setAddressInformation((prevState) => ({
                    ...prevState,
                    editHomeAddress: true,
                  }));
                }
                setCallLookupApiHome(true);
                handleAddressLineOneChange(newInputValue);
              }
            }}
            filterOptions={(options) => options}
            maxLength={30}
            autoFocus
            isBundle={isBundle(productName)}
            placeholder={t(
              isBundle(productName) ? 'Enter address line 1' : 'Enter Home Address Details',
              {
                defaultValue: isBundle(productName)
                  ? 'Enter address line 1'
                  : 'Enter Home Address Details',
              }
            )}
            fullWidth
            error={addressInformationErrors?.address1}
          />
          <Box component="span" role="alert" sx={classes.error_message}>
            {t(addressInformationErrors?.address1, {
              defaultValue: addressInformationErrors?.address1,
            })}
          </Box>
        </Grid>
      </Grid>

      <Grid container size={{ sm: 12 }} sx={classes.input_grid_addr}>
        <Grid size={{ xs: 12 }}>
          <CustomInput
            dataTestId="address_testid"
            id="address2"
            dataTestIdForLabel="homeAddress2"
            required={false}
            inputLabelAriaLabel={`${t(ADDRESS_LINE2, {
              defaultValue: ADDRESS_LINE2,
            })}`}
            label={t(ADDRESS_LINE2, {
              defaultValue: ADDRESS_LINE2,
            })}
            autoFocus={false}
            handleChange={handleChange}
            placeholder={t(
              isBundle(productName) ? 'Enter address line 2' : 'Enter Home Address Details',
              {
                defaultValue: isBundle(productName)
                  ? 'Enter address line 2'
                  : 'Enter Home Address Details',
              }
            )}
            fullWidth
            isBundle={isBundle(productName)}
            inputProps={{ maxLength: 30 }}
            value={commonTernary(addressInformation?.address2, addressInformation?.address2, '')}
            error={commonUtility(addressInformationErrors?.address2)}
            optional={isBundle(productName)}
          />
          <Box component="span" role="alert" sx={classes.error_message}>
            {t(addressInformationErrors?.address2, {
              defaultValue: addressInformationErrors?.address2,
            })}
          </Box>
        </Grid>
      </Grid>

      <Grid container size={{ sm: 12 }} sx={classes.input_grid_addr}>
        <Grid size={{ xs: 12 }}>
          <CustomSelect
            id="country"
            dataTestId="country_testid"
            dataTestIdForLabel="homeCountry"
            required={!isBundle(productName)}
            inputLabelAriaLabel={`${t(COUNTRY, {
              defaultValue: COUNTRY,
            })} ${t(REQUIRED, { defaultValue: REQUIRED })}`}
            label={
              <span
                style={{
                  color:
                    isBundle(productName) && addressInformationErrors?.country ? 'red' : 'inherit',
                }}
              >
                {commonTernary(
                  language === EN,
                  t(COUNTRY, { defaultValue: COUNTRY }),
                  isBundle(productName)
                    ? t(COUNTRY_ES_BUNDLE, { defaultValue: 'País' })
                    : t(COUNTRY_ES, { defaultValue: 'País/Región' })
                )}
              </span>
            }
            options={['PR', 'USA', 'VI']}
            handleChange={handleChange}
            fullWidth
            isBundle={isBundle(productName)}
            value={commonUtility(addressInformation?.country)}
            selectAriaLabel={`${t('Aria Label Country', {
              defaultValue: 'Country Required Choose your country dropdown menu button',
            })}`}
            error={mesgForCountrySel()}
            isInputEmpty={addressInformation?.country === ''}
          />
          <Box
            component="span"
            aria-live="polite"
            tabIndex={commonTernary(
              addressInformation?.country && addressInformation.country !== 'PR',
              '0',
              '-1'
            )}
            sx={classes.error_message}
          >
            {mesgForCountrySel()}
          </Box>
        </Grid>
      </Grid>

      <Grid container size={{ sm: 12 }} sx={classes.input_grid_addr}>
        <Grid size={{ xs: 12 }}>
          <CustomSelect
            id="state"
            // dataTestId="state_testid"
            dataTestIdForLabel="homeState"
            required={!isBundle(productName)}
            inputLabelAriaLabel={`${t(STATE, {
              defaultValue: STATE,
            })} ${t(REQUIRED, { defaultValue: REQUIRED })}`}
            label={
              <span
                style={{
                  color:
                    isBundle(productName) && addressInformationErrors?.state ? 'red' : 'inherit',
                }}
              >
                {t(STATE, { defaultValue: STATE })}
              </span>
            }
            options={returnStateList('state', addressInformation, personalInformation)}
            handleChange={handleChange}
            isState
            inputProps={{ 'data-testid': 'state_testid' }}
            fullWidth
            isBundle={isBundle(productName)}
            value={commonUtility(addressInformation?.state)}
            selectAriaLabel={`${t('Aria Label State', {
              defaultValue: 'State Required Choose your state dropdown menu button',
            })}`}
            error={addressInformationErrors?.state}
            isInputEmpty={addressInformation?.state === '' || addressInformation?.state === 'N/A'}
          />
          <Box component="span" role="alert" sx={classes.error_message}>
            {t(addressInformationErrors?.state, {
              defaultValue: addressInformationErrors?.state,
            })}
          </Box>
        </Grid>
      </Grid>

      <Grid container size={{ sm: 12 }} sx={classes.input_grid_addr}>
        <Grid size={{ xs: 12 }}>
          <CustomSelect
            id="city"
            dataTestIdForLabel="homeCity"
            required={!isBundle(productName)}
            inputLabelAriaLabel={`${t(CITY, {
              defaultValue: CITY,
            })} ${t(REQUIRED, {
              defaultValue: REQUIRED,
            })}`}
            label={
              <span
                style={{
                  color:
                    isBundle(productName) && addressInformationErrors?.city ? 'red' : 'inherit',
                }}
              >
                {t(CITY, { defaultValue: CITY })}
              </span>
            }
            options={returnCityList('city', addressInformation, personalInformation)}
            handleChange={handleChange}
            fullWidth
            isBundle={isBundle(productName)}
            value={commonUtility(addressInformation?.city)}
            selectAriaLabel={`${t('Aria Label City', {
              defaultValue: 'City Required Choose your city dropdown menu button',
            })}`}
            error={addressInformationErrors?.city}
            isInputEmpty={addressInformation?.city === ''}
          />
          <Box component="span" role="alert" sx={classes.error_message}>
            {t(addressInformationErrors?.city, {
              defaultValue: addressInformationErrors?.city,
            })}
          </Box>
        </Grid>
      </Grid>
      <Grid container size={{ sm: 12 }} sx={classes.input_grid_addr}>
        <Grid size={{ xs: 12 }}>
          <CustomInput
            id="zipCode"
            dataTestId="zipCode_id"
            required={!isBundle(productName)}
            value={commonTernary(addressInformation?.zipCode, addressInformation?.zipCode, '')}
            inputLabelAriaLabel={`${t(ZIPCODE, {
              defaultValue: ZIPCODE,
            })} ${t(REQUIRED, { defaultValue: REQUIRED })}`}
            label={
              <span
                style={{
                  color:
                    isBundle(productName) && addressInformationErrors?.zipCode ? 'red' : 'inherit',
                }}
              >
                {t(ZIPCODE, { defaultValue: ZIPCODE })}
              </span>
            }
            placeholder={t('Enter Zip Code', {
              defaultValue: 'Enter Zip Code',
            })}
            autoFocus={false}
            dataTestIdForLabel="homeZipCode"
            handleChange={handleChange}
            fullWidth
            isBundle={isBundle(productName)}
            error={commonUtility(addressInformationErrors?.zipCode)}
            inputProps={{ maxLength: flowTypeETB === 'EtbFlow' ? 9 : 5, inputMode: 'numeric' }}
          />
          <Box component="span" role="alert" sx={classes.error_message}>
            {t(addressInformationErrors?.zipCode, {
              defaultValue: addressInformationErrors?.zipCode,
            })}
          </Box>
        </Grid>
      </Grid>
    </>
  );
};

export default HomeAddress;

HomeAddress.propTypes = {
  handleChange: PropTypes.func,
  // isBundle: PropTypes.bool,
};
